import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, EmptyState, Icon, PageHeader } from '@/components/ui';
import { toast } from '@/components/ui/Toast';
import { agentApi, type BatchCall } from '@/shared/api/agentApi';
import { adminApi } from '@/shared/api/adminApi';
import { LeadDetailsPanel, type LeadLike } from '../components/LeadDetailsPanel';
import { LeadTimeline } from '../components/LeadTimeline';
import { AgentPerformance } from '../components/AgentPerformance';
import { DialBatchPanel } from '../components/DialBatchPanel';
import { CampaignDialControl } from '../components/CampaignDialControl';
import { useCustomerCard } from '../stores/customerCard';
import { useAutoRounds, type RoundPhase, type StopReason } from '../hooks/useAutoRounds';
import { useRingbackTone } from '../hooks/useRingbackTone';
import { useSipStore } from '../stores/sipStore';
import { WrapupInline, type DispositionOption, type ProductOption } from '../components/WrapupInline';
import { CallStatusBanner } from '../components/CallStatusBanner';

/**
 * מסך העבודה על קמפיין: שלושה פאנלים במקום העמודה הצרה של החייגן.
 *
 *   ימין  — פרטי הליד: כרטיס איש קשר, פעולות מהירות ושדות המידע.
 *   מרכז  — ציר הזמן של הליד + שורת הוספת הערה.
 *   שמאל  — פעולות: קוד סיום, מוצר, פולו-אפ, וסגירת הפנייה.
 *
 * המסך יושב בתוך מעטפת הנציג הקיימת (שלוש הלשוניות של האפיון) ואינו מוסיף
 * תפריט משלו. הוא נבנה לצד מסך החייגן הישן ולא במקומו, מאחורי דגל, כדי
 * שאפשר יהיה לחזור אחורה מיד.
 */

interface Campaign {
  id: number;
  name: string;
  dialer_mode?: string;
  /** ברירת המחדל היא מעבר אוטומטי; false = הנציג לוחץ "הליד הבא". */
  auto_dial_next?: boolean;
}

/**
 * "סבבים אוטומטיים": הדפדפן יורה dial-batch אחרי dial-batch עד שנגמרים
 * הלידים או שהנציג עוצר. רלוונטי לקמפיין שהשרת אינו מחייג בו לבד
 * (preview/ידני) וכשהמעבר האוטומטי דלוק.
 *
 * 🔴 הבאג שזה סוגר (15/09/2026): השרת אינו מגביל את dial-batch ל-preview —
 * `AgentDialerController::dialBatch` יורה סבב מקבילי של lines_per_agent
 * לכל קמפיין פעיל. נציג בקמפיין progressive/predictive ראה סבב אמיתי על
 * המסך ("3 חיוגים · 1 ענו") בזמן ש-roundMode היה false, ולכן אחרי
 * "שמור וסיים" המסך שלף **ליד בודד** במקום לירות את הסבב הבא. מבחוץ זה
 * נראה בדיוק כמו "סיכמתי ושום דבר לא קרה".
 *
 * ולכן: סבב חי (יש batch) הוא מצב-סבבים בפועל, יהיה מצב החיוג אשר יהיה.
 * `auto_dial_next=false` נשאר גובר — שם הנציג ביקש במפורש ללחוץ בעצמו.
 */
function isRoundMode(c: Campaign, hasLiveBatch = false): boolean {
  if (c.auto_dial_next === false) return false;

  return c.dialer_mode === 'preview' || hasLiveBatch;
}

/** מה קורה על הקווים בפועל, בלי קשר למצב הלולאה. */
type LiveLine = 'ringing' | 'in_call' | null;

function roundStatus(phase: RoundPhase, message: string | null, stopReason: StopReason | null, secondsLeft: number | null, live: LiveLine = null): { title: string; description?: string } | null {
  const left = secondsLeft !== null ? ` (${secondsLeft} שנ׳)` : '';
  // מצב הקווים גובר על מצב הלולאה. "הסבבים נעצרו" בזמן שלקוח עונה
  // (24/09/2026, קמפיין 26: בקר ההשהיה עצר את הלולאה שנייה אחרי הסבב)
  // הוא שקר לנציג — העצירה נוגעת לסבב *הבא*, לא לשיחה שעל הקו.
  const stoppedNote = phase === 'stopped' ? 'הסבבים נעצרו — לא ייצא סבב נוסף.' : null;
  if (live === 'in_call') {
    // סוכם לפני הניתוק — הלולאה ממתינה לסוף השיחה, וזה מה שהנציג צריך לדעת.
    const waitingNote = phase === 'waiting' && message ? message : null;
    return { title: 'בשיחה', description: stoppedNote ?? waitingNote ?? 'הסבב הבא ייצא אחרי סיכום השיחה.' };
  }
  if (live === 'ringing') {
    return { title: 'מצלצל ללקוחות…', description: stoppedNote ?? 'כשלקוח יענה השיחה תצלצל בסופטפון שלך.' };
  }
  switch (phase) {
    case 'dialing': return { title: 'מחייג סבב…' };
    case 'ringing': return { title: 'מצלצל ללקוחות…', description: 'כשלקוח יענה השיחה תצלצל בסופטפון שלך.' };
    case 'in_call': return { title: 'בשיחה', description: 'הסבב הבא ייצא אחרי סיכום השיחה.' };
    case 'cooldown': return { title: `אף אחד לא ענה — סבב הבא${left}`, description: message ?? undefined };
    case 'waiting': return { title: `ממתין ללידים — ניסיון הבא${left}`, description: message ?? undefined };
    case 'stopped': return {
      title: stopReason === 'exhausted' ? 'סיימת את כל הלידים בקמפיין' : 'הסבבים נעצרו',
      description: message ?? undefined,
    };
    default: return null;
  }
}

/**
 * מסביר **למה** הלידים חסומים, כי התגובה הנדרשת שונה:
 * "מיצו ניסיונות" מחייב החזרה לתור; "ממתינים לסולם" ייפתר מעצמו.
 */
function describeBlocked(b: { exhausted: number; deferred: number; daily_cap: number } | null): string {
  if (!b) return 'אפשר להחזיר אותם לתור ולהמשיך לחייג.';

  const parts: string[] = [];
  if (b.exhausted > 0) parts.push(`${b.exhausted} מיצו את מספר הניסיונות`);
  if (b.deferred > 0) parts.push(`${b.deferred} ממתינים לסולם החיוג`);
  if (b.daily_cap > 0) parts.push(`${b.daily_cap} חרגו מהתקרה היומית`);

  if (parts.length === 0) return 'אפשר להחזיר אותם לתור ולהמשיך לחייג.';

  // מי שרק ממתין — ייפתר מעצמו, ואין טעם לשלוח את המשתמש ללחוץ.
  const onlyWaiting = b.exhausted === 0 && b.daily_cap === 0;

  return parts.join(' · ') + (onlyWaiting
    ? '. הם יחזרו לתור מעצמם — או שאפשר להחזיר אותם עכשיו.'
    : '. אפשר להחזיר אותם לתור ולהמשיך לחייג.');
}

export function CampaignWorkspace() {
  // `?campaign=<id>` פותח ישירות על קמפיין — כך "חייג סבב" במסך הניהול
  // נוחת על מסך העבודה של אותו קמפיין ולא על רשימת הקמפיינים.
  const [params, setParams] = useSearchParams();
  const requested = Number(params.get('campaign')) || null;

  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [active, setActive] = useState<Campaign | null>(null);
  const [lead, setLead] = useState<LeadLike | null>(null);
  // כשהסבב פותח כמה קווים במקביל, כל הלידים שחויגו מוצגים — לא רק
  // הראשון. הנציג צריך לדעת למי המערכת מתקשרת ברגע זה.
  const [batchLeads, setBatchLeads] = useState<LeadLike[]>([]);
  const [dispositions, setDispositions] = useState<DispositionOption[]>([]);
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [exhausted, setExhausted] = useState(false);
  const [actionsTab, setActionsTab] = useState<'actions' | 'performance'>('actions');
  // כמה לידים חסומים על ידי תקרה — כדי שמסך "אין לידים" יסביר למה, ויציע
  // את התיקון במקום לשלוח את המשתמש למסך אחר.
  const [capped, setCapped] = useState(0);
  const [breakdown, setBreakdown] = useState<{ exhausted: number; deferred: number; daily_cap: number } | null>(null);
  // כרטיס הלקוח **לא** נפתח במסך הזה — לא צף ולא מוטמע. כשלקוח ענה, הנציג
  // צריך את ההיסטוריה (ציר הזמן) ואת פאנל הפעולות שלצדו, לא כרטיס שמחליף
  // אותם (07/09/2026: "אני לא רוצה שהמסך ישתנה לכרטיס הזה"). הפופ שמגיע
  // מ-useCustomerCardPop נסגר כאן מיד; שליטה בשיחה נשארת בסופטפון הצף.
  const cardOpen = useCustomerCard((s) => s.open);
  const closeCard = useCustomerCard((s) => s.close);
  const setSuppressFloating = useCustomerCard((s) => s.setSuppressFloating);
  useEffect(() => { if (cardOpen) closeCard(); }, [cardOpen, closeCard]);
  const [returning, setReturning] = useState(false);
  // סבב החיוג האחרון — נקרא מה-URL כדי ש"חייג סבב" במסך הניהול יוכל
  // להעביר אותו הלאה, והפאנל יציג את מי שחויג בפועל.
  const batch = params.get('batch');
  const paramsRef = useRef(params);
  paramsRef.current = params;
  const activeRef = useRef<Campaign | null>(null);
  activeRef.current = active;

  // סבבים אוטומטיים — הלולאה שיורה dial-batch אחרי dial-batch.
  const onBatch = useCallback((b: string) => {
    const next = new URLSearchParams(paramsRef.current);
    if (activeRef.current) next.set('campaign', String(activeRef.current.id));
    next.set('batch', b);
    setParams(next, { replace: true });
  }, [setParams]);
  const rounds = useAutoRounds({ onBatch });
  const roundsRef = useRef(rounds);
  roundsRef.current = rounds;
  // `batch` מגיע מה-URL וקיים כל עוד סבב חי על המסך — ולכן הוא הראיה
  // שיש סבב, גם בקמפיין שאינו preview.
  const roundMode = !!active && isRoundMode(active, !!batch);
  // שנייה-שנייה לספירה לאחור בבאנר, רק כשיש טיימר.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (rounds.nextAt === null) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [rounds.nextAt]);
  const secondsLeft = rounds.nextAt !== null ? Math.max(0, Math.ceil((rounds.nextAt - now) / 1000)) : null;
  const [batchRinging, setBatchRinging] = useState(false);
  const onCalls = useCallback((rows: BatchCall[]) => {
    setBatchLeads(rows.map((r) => ({ id: r.lead_id, phone: r.phone, name: r.name })));
    setBatchRinging(rows.some((r) => r.state === 'ringing'));
  }, []);
  // צליל חיוג כל עוד הסבב מצלצל ללקוחות ואף אחד עוד לא על הקו. ברגע
  // שלקוח עונה הסופטפון מצלצל/מדבר בעצמו — והצליל הזה משתתק כדי לא להתנגש.
  const sipState = useSipStore((s) => s.state);
  const sipBusy = sipState === 'ringing-in' || sipState === 'ringing-out' || sipState === 'on-call' || sipState === 'on-hold';
  useRingbackTone(!!batch && batchRinging && !sipBusy);
  // מצב הקווים בפועל: לקוח על הקו (סופטפון עסוק / ליד שענה בסבב הזה) או
  // קווים שעדיין מצלצלים — מוצג בבאנר לפני מצב הלולאה.
  const live: LiveLine = sipBusy || rounds.phase === 'in_call' || (!!batch && rounds.answeredLeadId !== null)
    ? 'in_call'
    : batchRinging ? 'ringing' : null;
  const status = roundMode ? roundStatus(rounds.phase, rounds.message, rounds.stopReason, secondsLeft, live) : null;

  useEffect(() => {
    setSuppressFloating(true);
    return () => setSuppressFloating(false);
  }, [setSuppressFloating]);

  useEffect(() => {
    void agentApi.dialerCampaigns()
      .then((c) => setCampaigns(c as Campaign[]))
      .catch(() => toast('שגיאה בטעינת הקמפיינים', 'danger'));
  }, []);

  // קודי הסיום והמוצרים נגזרים מהקמפיין הפעיל — כללים פר-קמפיין חייבים
  // להיטען מחדש בכל מעבר, אחרת נציג מתעד לפי הכללים של הקמפיין הקודם.
  useEffect(() => {
    if (!active) return;
    void agentApi.callDispositions(active.id)
      .then((d) => { if (d.length) setDispositions(d as DispositionOption[]); })
      .catch(() => {});
    void agentApi.agentProducts(active.id).then(setProducts).catch(() => setProducts([]));
  }, [active]);

  const fetchNext = useCallback(async (campaign: Campaign) => {
    setLoading(true);
    setExhausted(false);
    try {
      const next = await agentApi.dialerNext(campaign.id);
      if (!next) {
        setLead(null);
        setExhausted(true);
        // "אין לידים" יכול לנבוע ממיצוי ניסיונות ולא מסיום אמיתי. נבדק רק
        // כאן, כדי לא לשלם על שאילתה נוספת בכל שליפה מוצלחת.
        try {
          const r = await adminApi.cappedLeadsCount(campaign.id);
          setCapped(r.capped);
          setBreakdown(r.breakdown ?? null);
        } catch {
          setCapped(0); // למנהל בלבד יש גישה; לנציג רגיל פשוט לא יוצג הכפתור.
        }
        return;
      }
      setLead(next as LeadLike);
    } catch {
      toast('שגיאה בשליפת הליד הבא', 'danger');
    } finally {
      setLoading(false);
    }
  }, []);

  const start = useCallback(async (campaign: Campaign) => {
    setActive(campaign);
    // הקמפיין נשמר ב-URL כדי שרענון דף לא יזרוק את הנציג החוצה באמצע
    // עבודה, ושאפשר יהיה לשלוח קישור ישיר.
    // 🔑 שומרים את ה-batch: `setParams` דורס את כל ה-query, ובלעדיו
    // מזהה הסבב שהגיע מ"חייג סבב" נמחק — ואז פאנל הסבב וכרטיסי הלידים
    // המקבילים לא מוצגים בכלל.
    const keepBatch = paramsRef.current.get('batch');
    setParams(
      keepBatch ? { campaign: String(campaign.id), batch: keepBatch } : { campaign: String(campaign.id) },
      { replace: true },
    );
    if (isRoundMode(campaign, !!keepBatch)) {
      // סבבים: לא שולפים ליד יחיד (זה מה שייצר לידים נעולים בלי שיחה —
      // ליד 451), אלא יורים סבב; סבב שכבר נורה ב"חייג סבב" של המנהל מאומץ.
      setLead(null);
      setExhausted(false);
      if (keepBatch) roundsRef.current.adoptBatch(campaign.id, keepBatch);
      else roundsRef.current.start(campaign.id);
      return;
    }
    await fetchNext(campaign);
  }, [fetchNext, setParams]);

  // הלידים של הסבב מגיעים מהפאנל (onCalls) — poller אחד לשניהם.
  useEffect(() => { if (!batch) { setBatchLeads([]); setBatchRinging(false); } }, [batch]);

  // בסבבים, הליד לעבודה הוא מי שענה — לא ליד נוסף שנשלף לצד הסבב.
  useEffect(() => {
    if (!roundMode || rounds.answeredLeadId === null) return;
    const l = batchLeads.find((b) => b.id === rounds.answeredLeadId);
    if (l) setLead(l);
  }, [roundMode, rounds.answeredLeadId, batchLeads]);

  // "כולם טופלו" בסבבים: אותו מסך חסומים כמו בשליפה יחידה.
  useEffect(() => {
    if (!roundMode || !active || rounds.phase !== 'stopped' || rounds.stopReason !== 'exhausted') return;
    adminApi.cappedLeadsCount(active.id)
      .then((r) => { setCapped(r.capped); setBreakdown(r.breakdown ?? null); })
      .catch(() => setCapped(0));
  }, [roundMode, active, rounds.phase, rounds.stopReason]);

  // המונה מתרענן כל עוד המסך תקוע על "אין לידים": שיחה שהסתיימה ברקע
  // משנה את המספר, ומסך שמציג נתון מלפני חמש דקות שולח את המשתמש לתקן
  // בעיה שכבר לא קיימת (או להפך).
  useEffect(() => {
    if (!exhausted || !active) return;
    const t = setInterval(() => {
      adminApi.cappedLeadsCount(active.id)
        .then((r) => { setCapped(r.capped); setBreakdown(r.breakdown ?? null); })
        .catch(() => {});
    }, 10000);
    return () => clearInterval(t);
  }, [exhausted, active]);

  // כניסה עם ?campaign=<id> — נפתח עליו ברגע שהרשימה נטענה.
  useEffect(() => {
    if (!requested || active) return;
    const match = campaigns.find((c) => c.id === requested);
    if (match) void start(match);
  }, [requested, active, campaigns, start]);

  /** מחזיר את החסומים ומיד מנסה לשלוף שוב — הנציג נשאר במסך וממשיך. */
  async function returnCappedAndRetry() {
    if (!active) return;
    setReturning(true);
    try {
      const r = await adminApi.returnCappedLeads(active.id);
      toast(r.message ?? `${r.returned} לידים הוחזרו לתור`, 'ok');
      setCapped(0);
      if (roundMode) rounds.resume(active.id);
      else await fetchNext(active);
    } catch {
      toast('שגיאה בהחזרת הלידים', 'danger');
    } finally {
      setReturning(false);
    }
  }

  async function saveWrapup(d: {
    disposition: string; notes: string; callback_at: string | null;
    tags: string[]; reason_code?: string | null; product_id?: number | null;
    callback_phone?: string | null; callback_name?: string | null;
  }) {
    if (!lead || !active) return;
    const def = dispositions.find((x) => x.code === d.disposition);
    const nextStatus = d.disposition === 'dnc' ? 'dnc' : def?.requires_followup ? 'queued' : 'completed';

    await agentApi.dialerDispose(
      lead.id, d.disposition, d.callback_at || undefined, nextStatus, d.notes || undefined,
      {
        product_id: d.product_id ?? null, reason_code: d.reason_code ?? null,
        callback_phone: d.callback_phone ?? null, callback_name: d.callback_name ?? null,
      },
    );
    toast('נשמר', 'ok');
    // מכבד את הגדרת הקמפיין, בדיוק כמו מסך החייגן הישן. קודם המסך הזה
    // תמיד המשיך הלאה — התנהגות שונה לאותה הגדרה בשני מסכים.
    if (roundMode) {
      // סבבים: אחרי הסיכום יוצא הסבב הבא, לא ליד יחיד.
      setLead(null);
      rounds.afterWrapup();
      return;
    }
    if (active.auto_dial_next !== false) {
      await fetchNext(active);
    }
  }

  // הליד שנשלף לעבודה ראשון, ואחריו שאר הלידים של הסבב — בלי כפילות.
  const panelLeads: LeadLike[] = lead
    ? [lead, ...batchLeads.filter((b) => b.id !== lead.id)]
    : batchLeads;

  if (!active) {
    if (campaigns.length === 0) {
      return (
        <div style={{ padding: 'var(--gap)' }}>
          <EmptyState icon="target" title="אין קמפיינים פעילים"
            description="המתן עד שמנהל יוסיף אותך לקמפיין." />
        </div>
      );
    }

    return (
      <div className="stack" style={{ padding: 'var(--gap)' }}>
        <PageHeader title="קמפיינים" />
        {campaigns.map((c) => (
          <Card key={c.id}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <strong>{c.name}</strong>
              <button type="button" className="btn primary" onClick={() => void start(c)}>
                <Icon name="play" size={15} /> הפעלת חייגן אוטומטי
              </button>
            </div>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* כותרת ההפעלה */}
      <div style={{
        background: 'var(--ok)', color: 'var(--on-accent, #fff)',
        padding: '10px var(--gap)', display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <Icon name="play" size={16} />
        <strong>הפעלת חייגן אוטומטי — {active.name}</strong>
        {status && (
          <span data-testid="round-status" style={{ fontSize: 'var(--t-sm)', opacity: 0.9 }}>
            · {status.title}
          </span>
        )}
        <div style={{ marginInlineStart: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* עצירת הסבבים — הלולאה חיה בדפדפן, אבל העצירה חייבת להגיע גם
              לשרת: הסבב שכבר יצא מצלצל אצל כמה לקוחות במקביל, ובלי ניתוק
              שלהם "עצור" לא נראה כמו עצירה (ראו useAutoRounds.stop). */}
          {roundMode && (rounds.active ? (
            <button type="button" className="btn btn-sm" onClick={() => rounds.stop('manual', 'הסבבים נעצרו על ידך.')}>
              עצור סבבים
            </button>
          ) : (
            <button type="button" className="btn btn-sm" onClick={() => rounds.resume(active.id)}>
              המשך סבבים
            </button>
          ))}
          {/* מעבר ידני לליד הבא — מוצג רק כשהמעבר האוטומטי כבוי, אחרת
              הוא כפתור שמתחרה בהתנהגות שכבר קורית לבד. */}
          {active.auto_dial_next === false && (
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => void fetchNext(active)}
              title="המעבר האוטומטי כבוי בקמפיין הזה — שליפת הליד הבא"
            >
              הליד הבא ←
            </button>
          )}
          {/* עצור/המשך — עצירה דורשת סיבה (5.3). */}
          <CampaignDialControl
            campaignId={active.id}
            onChange={(paused) => { if (paused) rounds.stop('paused', 'החייגן מושהה — הסבבים נעצרו.'); }}
          />
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => { rounds.stop('manual'); setActive(null); setLead(null); setParams({}, { replace: true }); }}
          >
            יציאה מהקמפיין
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 'var(--gap)' }}><Card>טוען ליד…</Card></div>
      ) : roundMode && !lead ? (
        /* סבבים בלי שיחה חיה: הסבב ומצבו — ולא "סיימת את כל הלידים" בזמן
           שהמערכת עדיין מחייגת. */
        <div style={{
          padding: 'var(--gap)', display: 'grid', gap: 'var(--gap)',
          gridTemplateColumns: 'minmax(240px, 360px) minmax(0, 1fr)', alignItems: 'start',
        }}>
          <div>
            <DialBatchPanel campaignId={active.id} batch={batch} onSettled={rounds.onSettled} onCalls={onCalls} />
            {batchLeads.map((l, i) => (
              <Card key={l.id} style={i > 0 ? { marginTop: 'var(--gap)' } : undefined}>
                <LeadDetailsPanel lead={l} />
              </Card>
            ))}
          </div>
          <div>
            {rounds.phase === 'stopped' && rounds.stopReason === 'exhausted' && capped > 0 ? (
              <EmptyState
                icon="warn"
                title={`${capped} לידים חסומים ולא נשלפים`}
                description={describeBlocked(breakdown)}
                action={(
                  <button type="button" className="btn primary" disabled={returning} onClick={() => void returnCappedAndRetry()}>
                    {returning ? 'מחזיר…' : `החזרת ${capped} לידים לתור`}
                  </button>
                )}
              />
            ) : status ? (
              <EmptyState
                icon={live ? 'phoneOut' : rounds.phase === 'stopped' ? (rounds.stopReason === 'exhausted' ? 'ok' : 'warn') : 'phoneOut'}
                title={status.title}
                description={status.description}
                action={rounds.phase === 'stopped' && rounds.stopReason !== 'exhausted' ? (
                  <button type="button" className="btn primary" onClick={() => rounds.resume(active.id)}>המשך סבבים</button>
                ) : undefined}
              />
            ) : null}
          </div>
        </div>
      ) : exhausted || !lead ? (
        <div style={{ padding: 'var(--gap)' }}>
          {capped > 0 ? (
            <EmptyState
              icon="warn"
              title={`${capped} לידים חסומים ולא נשלפים`}
              description={describeBlocked(breakdown)}
              action={(
                <button
                  type="button"
                  className="btn primary"
                  disabled={returning}
                  onClick={() => void returnCappedAndRetry()}
                >
                  {returning ? 'מחזיר…' : `החזרת ${capped} לידים לתור`}
                </button>
              )}
            />
          ) : (
            <EmptyState icon="ok" title="סיימת את כל הלידים בקמפיין"
              description="אין כרגע לידים נוספים לחיוג." />
          )}
        </div>
      ) : (
        <>
        {/* מי על הקו ומי ניתק — במרכז, מעל שלושת הפאנלים (06/10/2026). */}
        <CallStatusBanner name={lead.name} phone={lead.phone} />
        <div style={{
          flex: 1, minHeight: 0, display: 'grid', gap: 'var(--gap)',
          gridTemplateColumns: 'minmax(240px, 300px) minmax(0, 1fr) minmax(240px, 320px)',
          padding: 'var(--gap)', alignItems: 'start',
        }}>
          {/* ימין — סבב החיוג ופרטי הלידים שחויגו */}
          <div>
            {active && <DialBatchPanel campaignId={active.id} batch={batch} onSettled={rounds.onSettled} onCalls={onCalls} />}
            {/* סבב מקבילי: כרטיס לכל ליד שחויג. הליד שנשלף לעבודה מוצג
                ראשון, והשאר אחריו — כדי שהנציג יראה למי עוד מתקשרים. */}
            {panelLeads.map((l, i) => (
              <Card key={l.id} style={i > 0 ? { marginTop: 'var(--gap)' } : undefined}>
                <LeadDetailsPanel lead={l} />
              </Card>
            ))}
          </div>

          {/* מרכז — ציר הזמן של הליד, גם בזמן שיחה. כרטיס הלקוח אינו נפתח
              כאן (ראה למעלה): ההיסטוריה היא מה שהנציג צריך מול לקוח שענה. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap)', minWidth: 0 }}>
            <Card padded={false}>
              <div style={{ padding: 'var(--pad-card)', height: 'min(70vh, 620px)' }}>
                <LeadTimeline leadId={lead.id} />
              </div>
            </Card>
          </div>

          {/* שמאל — פעולות / הביצועים שלי */}
          <Card padded={false}>
            <div style={{ display: 'flex', borderBottom: '1px solid var(--line)' }}>
              {([['actions', 'פעולות'], ['performance', 'הביצועים שלי']] as const).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setActionsTab(id)}
                  aria-pressed={actionsTab === id}
                  style={{
                    flex: 1, padding: '10px 8px', cursor: 'pointer', background: 'none',
                    border: 'none', fontSize: 'var(--t-sm)',
                    fontWeight: actionsTab === id ? 700 : 500,
                    color: actionsTab === id ? 'var(--accent)' : 'var(--text-2)',
                    borderBottom: `2px solid ${actionsTab === id ? 'var(--accent)' : 'transparent'}`,
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            <div style={{ padding: 'var(--pad-card)' }}>
              {actionsTab === 'actions' ? (
                <WrapupInline
                  // key + draftKey לפי ליד: טופס נקי לכל ליד, ובחירה שכבר נעשתה
                  // שורדת רינדור מחדש של המסך. אין "דלג" — כל שיחה מתועדת.
                  key={lead.id}
                  draftKey={`lead:${lead.id}`}
                  dispositions={dispositions}
                  products={products}
                  onSave={saveWrapup}
                  chrome={false}
                  phoneNumber={lead.phone}
                />
              ) : (
                <AgentPerformance />
              )}

            </div>
          </Card>
        </div>
        </>
      )}
    </div>
  );
}
